<template>
  <BaseModal
    :is-open="isOpen"
    :title="modalTitle"
    max-width="600px"
    :closable="false"
  >
    <div class="seat-selection-container">
      <p class="selection-prompt">{{ promptText }}</p>

      <!-- Seat Grid - shows all seats with their current occupants -->
      <div class="seats-grid">
        <button
          v-for="seatInfo in displaySeats"
          :key="seatInfo.seat"
          @click="handleSelectSeat(seatInfo.seat)"
          class="seat-button"
          :class="{
            'seat-selected': selectedSeat === seatInfo.seat,
            'seat-bot': seatInfo.is_bot,
            'seat-human': !seatInfo.is_bot && seatInfo.occupied,
            'seat-empty': !seatInfo.occupied
          }"
          :disabled="!seatInfo.is_bot && seatInfo.occupied"
        >
          <div class="seat-icon" :class="getTeamClass(seatInfo.seat)">
            <span class="material-symbols-outlined">{{ getSeatIcon(seatInfo) }}</span>
          </div>
          <div class="seat-info">
            <span class="seat-label">{{ getSeatLabel(seatInfo) }}</span>
            <span class="seat-team">{{ getTeamName(seatInfo.seat) }}</span>
            <span v-if="seatInfo.is_bot" class="seat-status bot">Bot - Can Replace</span>
            <span v-else-if="seatInfo.occupied" class="seat-status human">Human Player</span>
            <span v-else class="seat-status empty">Empty</span>
          </div>
        </button>
      </div>

      <!-- Player being assigned info -->
      <div v-if="playerToAssign" class="player-assign-info">
        <span class="assign-label">Assigning:</span>
        <span class="assign-name">{{ playerToAssign.username }}</span>
      </div>

      <!-- Confirm Button -->
      <button
        @click="handleConfirm"
        :disabled="selectedSeat === null"
        class="btn-confirm"
      >
        <span class="material-symbols-outlined">check_circle</span>
        <span>Confirm Seat Selection</span>
      </button>
    </div>
  </BaseModal>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import BaseModal from './BaseModal.vue'

/**
 * SeatSelectionModal Component
 *
 * Allows creator to assign a joining player to a seat (replacing a bot).
 * Shows all seats with their current occupants.
 *
 * Props:
 * - data.seats: Array of seat info objects
 * - data.teams: Team configuration
 * - data.player_to_assign: Player being assigned to a seat
 *
 * Emits:
 * - select: { seat: number } - Selected seat index
 * - close: [] - Modal closed without selection
 */

interface SeatInfo {
  seat: number
  occupied: boolean
  player: string | null
  is_bot: boolean
}

interface PlayerToAssign {
  username: string
  user_id: string
  socket_id: string
}

interface Props {
  isOpen: boolean
  data: {
    seats?: SeatInfo[]
    teams?: {
      A?: { name: string; seats: number[] }
      B?: { name: string; seats: number[] }
    }
    player_to_assign?: PlayerToAssign
    available_seats?: number[] // Legacy support
  } | null
}

const props = withDefaults(defineProps<Props>(), {
  isOpen: false,
  data: null,
})

const emit = defineEmits<{
  select: [{ seat: number }]
  close: []
}>()

const selectedSeat = ref<number | null>(null)

/**
 * Modal title - changes based on context
 */
const modalTitle = computed(() => {
  return props.data?.player_to_assign ? 'Assign Player to Seat' : 'Choose Your Seat'
})

/**
 * Prompt text
 */
const promptText = computed(() => {
  if (props.data?.player_to_assign) {
    return `Select which bot seat to replace with ${props.data.player_to_assign.username}:`
  }
  return 'Select which seat you\'d like to take:'
})

/**
 * Player being assigned (for creator assigning another player)
 */
const playerToAssign = computed(() => {
  return props.data?.player_to_assign || null
})

/**
 * Display seats - handles both new and legacy data formats
 */
const displaySeats = computed((): SeatInfo[] => {
  // New format: seats array with full info
  if (props.data?.seats && Array.isArray(props.data.seats)) {
    return props.data.seats
  }
  // Legacy format: available_seats array of numbers
  if (props.data?.available_seats) {
    return props.data.available_seats.map(seat => ({
      seat,
      occupied: false,
      player: null,
      is_bot: false
    }))
  }
  return []
})

/**
 * Get icon for seat
 */
function getSeatIcon(seatInfo: SeatInfo): string {
  if (seatInfo.is_bot) return 'smart_toy'
  if (seatInfo.occupied) return 'person'
  return 'person_outline'
}

/**
 * Get seat label showing player name or seat number
 */
function getSeatLabel(seatInfo: SeatInfo): string {
  if (seatInfo.player) {
    return seatInfo.player
  }
  return `Seat ${seatInfo.seat + 1}`
}

/**
 * Get team name for seat
 */
function getTeamName(seat: number): string {
  const teams = props.data?.teams
  if (teams?.A?.seats?.includes(seat)) {
    return teams.A.name || 'Team A'
  }
  if (teams?.B?.seats?.includes(seat)) {
    return teams.B.name || 'Team B'
  }
  // Fallback: odd seats = Team A, even seats = Team B (for this game's layout)
  return (seat === 1 || seat === 3) ? 'Team A' : 'Team B'
}

/**
 * Get team CSS class for seat
 */
function getTeamClass(seat: number): string {
  const teams = props.data?.teams
  if (teams?.A?.seats?.includes(seat)) return 'team-a'
  if (teams?.B?.seats?.includes(seat)) return 'team-b'
  return (seat === 1 || seat === 3) ? 'team-a' : 'team-b'
}

/**
 * Handle seat selection
 */
function handleSelectSeat(seat: number): void {
  selectedSeat.value = seat
}

/**
 * Confirm seat selection
 */
function handleConfirm(): void {
  if (selectedSeat.value !== null) {
    emit('select', { seat: selectedSeat.value })
    selectedSeat.value = null
  }
}
</script>

<style scoped>
.seat-selection-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  padding: 0.5rem;
}

/* Selection Prompt */
.selection-prompt {
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.9);
  text-align: center;
  margin: 0;
}

/* Seats Grid */
.seats-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  width: 100%;
  max-width: 500px;
}

/* Seat Button */
.seat-button {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem;
  border-radius: 12px;
  border: 2px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
  cursor: pointer;
  transition: all 0.2s;
}

.seat-button:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(232, 197, 71, 0.4);
  transform: translateY(-2px);
}

.seat-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.seat-button.seat-selected {
  background: linear-gradient(135deg, rgba(232, 197, 71, 0.2), rgba(212, 175, 55, 0.2));
  border-color: #e8c547;
  box-shadow: 0 0 20px rgba(232, 197, 71, 0.3);
}

/* Bot seats - highlight as selectable */
.seat-button.seat-bot {
  border-color: rgba(16, 185, 129, 0.3);
  background: rgba(16, 185, 129, 0.1);
}

.seat-button.seat-bot:hover:not(:disabled) {
  border-color: rgba(16, 185, 129, 0.6);
  background: rgba(16, 185, 129, 0.2);
}

/* Human seats - show as unavailable */
.seat-button.seat-human {
  border-color: rgba(239, 68, 68, 0.2);
  background: rgba(239, 68, 68, 0.05);
}

/* Seat Icon */
.seat-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.seat-icon.team-a {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.3), rgba(37, 99, 235, 0.3));
}

.seat-icon.team-a .material-symbols-outlined {
  color: #60a5fa;
}

.seat-icon.team-b {
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.3), rgba(220, 38, 38, 0.3));
}

.seat-icon.team-b .material-symbols-outlined {
  color: #f87171;
}

.seat-icon .material-symbols-outlined {
  font-size: 1.75rem;
}

/* Seat Info */
.seat-info {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.125rem;
  flex: 1;
}

.seat-label {
  font-size: 1.125rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
}

.seat-team {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.6);
}

.seat-status {
  font-size: 0.75rem;
  padding: 0.125rem 0.5rem;
  border-radius: 4px;
  margin-top: 0.25rem;
}

.seat-status.bot {
  background: rgba(16, 185, 129, 0.2);
  color: #34d399;
}

.seat-status.human {
  background: rgba(239, 68, 68, 0.2);
  color: #f87171;
}

.seat-status.empty {
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.6);
}

/* Player assign info */
.player-assign-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  background: rgba(232, 197, 71, 0.1);
  border: 1px solid rgba(232, 197, 71, 0.3);
  border-radius: 8px;
}

.assign-label {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.7);
}

.assign-name {
  font-size: 1rem;
  font-weight: 600;
  color: #e8c547;
}

/* Confirm Button */
.btn-confirm {
  padding: 0.875rem 2rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border: none;
  background: linear-gradient(135deg, #e8c547 0%, #d4af37 100%);
  color: #0d1f2d;
  box-shadow: 0 4px 12px rgba(232, 197, 71, 0.3);
  margin-top: 0.5rem;
}

.btn-confirm:hover:not(:disabled) {
  background: linear-gradient(135deg, #f0d060 0%, #e0bf47 100%);
  box-shadow: 0 6px 16px rgba(232, 197, 71, 0.4);
  transform: translateY(-2px);
}

.btn-confirm:active:not(:disabled) {
  transform: translateY(0);
  box-shadow: 0 2px 8px rgba(232, 197, 71, 0.3);
}

.btn-confirm:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.5);
  box-shadow: none;
}

.btn-confirm .material-symbols-outlined {
  font-size: 1.25rem;
}

/* Responsive */
@media (max-width: 640px) {
  .seats-grid {
    grid-template-columns: 1fr;
  }
}
</style>
